import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import * as CoreService from '../services/CoreService';
import { clearPendingRequests } from '../utils/apiClient';
import usePageVisibility from '../hooks/usePageVisibility';

// Action types
const ACTIONS = {
  SET_PROJECTS_LOADING: 'SET_PROJECTS_LOADING',
  SET_PROJECTS_SUCCESS: 'SET_PROJECTS_SUCCESS',
  SET_PROJECTS_ERROR: 'SET_PROJECTS_ERROR',
  SET_TARGETS_LOADING: 'SET_TARGETS_LOADING',
  SET_TARGETS_SUCCESS: 'SET_TARGETS_SUCCESS',
  SET_TARGETS_ERROR: 'SET_TARGETS_ERROR',
  SET_HEADS_LOADING: 'SET_HEADS_LOADING',
  SET_HEADS_SUCCESS: 'SET_HEADS_SUCCESS',
  SET_HEADS_ERROR: 'SET_HEADS_ERROR',
  SET_MEMORY_REPORT_LOADING: 'SET_MEMORY_REPORT_LOADING',
  SET_MEMORY_REPORT_SUCCESS: 'SET_MEMORY_REPORT_SUCCESS',
  SET_MEMORY_REPORT_ERROR: 'SET_MEMORY_REPORT_ERROR',
  SET_COMMIT_MESSAGE_LOADING: 'SET_COMMIT_MESSAGE_LOADING',
  SET_COMMIT_MESSAGE_SUCCESS: 'SET_COMMIT_MESSAGE_SUCCESS',
  SET_COMMIT_MESSAGE_ERROR: 'SET_COMMIT_MESSAGE_ERROR',
  SET_MEMORY_COMPARISON_LOADING: 'SET_MEMORY_COMPARISON_LOADING',
  SET_MEMORY_COMPARISON_SUCCESS: 'SET_MEMORY_COMPARISON_SUCCESS',
  SET_MEMORY_COMPARISON_ERROR: 'SET_MEMORY_COMPARISON_ERROR',
  SET_BUDGETS_LOADING: 'SET_BUDGETS_LOADING',
  SET_BUDGETS_SUCCESS: 'SET_BUDGETS_SUCCESS',
  SET_BUDGETS_ERROR: 'SET_BUDGETS_ERROR',
  SET_CREATED_PROJECT: 'SET_CREATED_PROJECT',
  CLEAR_CREATED_PROJECT: 'CLEAR_CREATED_PROJECT',
  ADD_PROJECT: 'ADD_PROJECT',
  REMOVE_PROJECT: 'REMOVE_PROJECT',
  CLEAR_ALL_DATA: 'CLEAR_ALL_DATA'
};

// Initial state
const initialState = {
  projects: [],
  targets: {}, // projectId -> targets[]
  heads: {}, // `${projectId}-${targetName}` -> heads[]
  memoryReports: {}, // commitSha -> full memory report
  commitMessages: {}, // commitSha -> commit metadata
  memoryComparisons: {}, // `${baseCommitSha}-${headCommitSha}` -> comparison data
  budgets: {}, // `${projectId}-${targetName}-${commitSha}` -> budgets[]
  // Project creation state
  createdProject: null,
  loading: {
    projects: false,
    targets: {}, // projectId -> boolean
    heads: {}, // `${projectId}-${targetName}` -> boolean
    memoryReports: {}, // commitSha -> boolean
    commitMessages: {}, // commitSha -> boolean
    memoryComparisons: {}, // `${baseCommitSha}-${headCommitSha}` -> boolean
    budgets: {} // key -> boolean
  },
  errors: {
    projects: null,
    targets: {}, // projectId -> error
    heads: {}, // `${projectId}-${targetName}` -> error
    memoryReports: {}, // commitSha -> error
    commitMessages: {}, // commitSha -> error
    memoryComparisons: {}, // `${baseCommitSha}-${headCommitSha}` -> error
    budgets: {} // key -> error
  }
};

// Reducer
function projectReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_PROJECTS_LOADING:
      return {
        ...state,
        loading: { ...state.loading, projects: action.loading },
        errors: { ...state.errors, projects: action.loading ? null : state.errors.projects }
      };

    case ACTIONS.SET_PROJECTS_SUCCESS:
      return {
        ...state,
        projects: action.projects,
        loading: { ...state.loading, projects: false },
        errors: { ...state.errors, projects: null }
      };

    case ACTIONS.SET_PROJECTS_ERROR:
      return {
        ...state,
        loading: { ...state.loading, projects: false },
        errors: { ...state.errors, projects: action.error }
      };

    case ACTIONS.SET_TARGETS_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          targets: { ...state.loading.targets, [action.projectId]: action.loading }
        },
        errors: {
          ...state.errors,
          targets: {
            ...state.errors.targets,
            [action.projectId]: action.loading ? null : state.errors.targets[action.projectId]
          }
        }
      };

    case ACTIONS.SET_TARGETS_SUCCESS:
      return {
        ...state,
        targets: { ...state.targets, [action.projectId]: action.targets },
        loading: {
          ...state.loading,
          targets: { ...state.loading.targets, [action.projectId]: false }
        },
        errors: {
          ...state.errors,
          targets: { ...state.errors.targets, [action.projectId]: null }
        }
      };

    case ACTIONS.SET_TARGETS_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          targets: { ...state.loading.targets, [action.projectId]: false }
        },
        errors: {
          ...state.errors,
          targets: { ...state.errors.targets, [action.projectId]: action.error }
        }
      };

    case ACTIONS.SET_HEADS_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          heads: { ...state.loading.heads, [action.key]: action.loading }
        },
        errors: {
          ...state.errors,
          heads: {
            ...state.errors.heads,
            [action.key]: action.loading ? null : state.errors.heads[action.key]
          }
        }
      };

    case ACTIONS.SET_HEADS_SUCCESS:
      return {
        ...state,
        heads: { ...state.heads, [action.key]: action.heads },
        loading: {
          ...state.loading,
          heads: { ...state.loading.heads, [action.key]: false }
        },
        errors: {
          ...state.errors,
          heads: { ...state.errors.heads, [action.key]: null }
        }
      };

    case ACTIONS.SET_HEADS_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          heads: { ...state.loading.heads, [action.key]: false }
        },
        errors: {
          ...state.errors,
          heads: { ...state.errors.heads, [action.key]: action.error }
        }
      };

    case ACTIONS.SET_MEMORY_REPORT_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          memoryReports: { ...state.loading.memoryReports, [action.commitSha]: action.loading }
        },
        errors: {
          ...state.errors,
          memoryReports: {
            ...state.errors.memoryReports,
            [action.commitSha]: action.loading ? null : state.errors.memoryReports[action.commitSha]
          }
        }
      };

    case ACTIONS.SET_MEMORY_REPORT_SUCCESS:
      return {
        ...state,
        memoryReports: { ...state.memoryReports, [action.commitSha]: action.report },
        loading: {
          ...state.loading,
          memoryReports: { ...state.loading.memoryReports, [action.commitSha]: false }
        },
        errors: {
          ...state.errors,
          memoryReports: { ...state.errors.memoryReports, [action.commitSha]: null }
        }
      };

    case ACTIONS.SET_MEMORY_REPORT_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          memoryReports: { ...state.loading.memoryReports, [action.commitSha]: false }
        },
        errors: {
          ...state.errors,
          memoryReports: { ...state.errors.memoryReports, [action.commitSha]: action.error }
        }
      };

    case ACTIONS.SET_COMMIT_MESSAGE_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          commitMessages: { ...state.loading.commitMessages, [action.commitSha]: action.loading }
        },
        errors: {
          ...state.errors,
          commitMessages: {
            ...state.errors.commitMessages,
            [action.commitSha]: action.loading ? null : state.errors.commitMessages[action.commitSha]
          }
        }
      };

    case ACTIONS.SET_COMMIT_MESSAGE_SUCCESS:
      return {
        ...state,
        commitMessages: { ...state.commitMessages, [action.commitSha]: action.message },
        loading: {
          ...state.loading,
          commitMessages: { ...state.loading.commitMessages, [action.commitSha]: false }
        },
        errors: {
          ...state.errors,
          commitMessages: { ...state.errors.commitMessages, [action.commitSha]: null }
        }
      };

    case ACTIONS.SET_COMMIT_MESSAGE_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          commitMessages: { ...state.loading.commitMessages, [action.commitSha]: false }
        },
        errors: {
          ...state.errors,
          commitMessages: { ...state.errors.commitMessages, [action.commitSha]: action.error }
        }
      };

    case ACTIONS.SET_MEMORY_COMPARISON_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          memoryComparisons: { ...state.loading.memoryComparisons, [action.comparisonKey]: action.loading }
        },
        errors: {
          ...state.errors,
          memoryComparisons: {
            ...state.errors.memoryComparisons,
            [action.comparisonKey]: action.loading ? null : state.errors.memoryComparisons[action.comparisonKey]
          }
        }
      };

    case ACTIONS.SET_MEMORY_COMPARISON_SUCCESS:
      return {
        ...state,
        memoryComparisons: { ...state.memoryComparisons, [action.comparisonKey]: action.data },
        loading: {
          ...state.loading,
          memoryComparisons: { ...state.loading.memoryComparisons, [action.comparisonKey]: false }
        },
        errors: {
          ...state.errors,
          memoryComparisons: { ...state.errors.memoryComparisons, [action.comparisonKey]: null }
        }
      };

    case ACTIONS.SET_MEMORY_COMPARISON_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          memoryComparisons: { ...state.loading.memoryComparisons, [action.comparisonKey]: false }
        },
        errors: {
          ...state.errors,
          memoryComparisons: { ...state.errors.memoryComparisons, [action.comparisonKey]: action.error }
        }
      };

    case ACTIONS.SET_CREATED_PROJECT:
      return {
        ...state,
        createdProject: action.project
      };

    case ACTIONS.CLEAR_CREATED_PROJECT:
      return {
        ...state,
        createdProject: null
      };

    case ACTIONS.ADD_PROJECT:
      return {
        ...state,
        projects: state.projects ? [...state.projects, action.project] : [action.project]
      };

    case ACTIONS.REMOVE_PROJECT:
      const updatedProjects = state.projects ? state.projects.filter(p => p.id !== action.projectId) : [];
      return {
        ...state,
        projects: updatedProjects,
        // Clear project-related data
        targets: Object.fromEntries(
          Object.entries(state.targets).filter(([key]) => key !== action.projectId)
        )
      };

    case ACTIONS.SET_BUDGETS_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          budgets: { ...state.loading.budgets, [action.key]: action.loading }
        },
        errors: {
          ...state.errors,
          budgets: {
            ...state.errors.budgets,
            [action.key]: action.loading ? null : state.errors.budgets[action.key]
          }
        }
      };

    case ACTIONS.SET_BUDGETS_SUCCESS:
      return {
        ...state,
        budgets: { ...state.budgets, [action.key]: action.budgets },
        loading: {
          ...state.loading,
          budgets: { ...state.loading.budgets, [action.key]: false }
        },
        errors: {
          ...state.errors,
          budgets: { ...state.errors.budgets, [action.key]: null }
        }
      };

    case ACTIONS.SET_BUDGETS_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          budgets: { ...state.loading.budgets, [action.key]: false }
        },
        errors: {
          ...state.errors,
          budgets: { ...state.errors.budgets, [action.key]: action.error }
        }
      };

    case ACTIONS.CLEAR_ALL_DATA:
      return {
        ...initialState,
        projects: state.projects,
        createdProject: state.createdProject
      };

    default:
      return state;
  }
}

// Create context
const ProjectContext = createContext();

// Provider component
export function ProjectProvider({ children }) {
  const [state, dispatch] = useReducer(projectReducer, initialState);

  // Page visibility detection
  const { checkWasRecentlyHidden } = usePageVisibility();
  const mountedRef = useRef(false);

  // Request deduplication map
  const pendingRequests = useRef(new Map());

  // Deduplicated request wrapper
  const makeRequest = useCallback(async (key, requestFn) => {
    if (pendingRequests.current.has(key)) {
      return pendingRequests.current.get(key);
    }

    const request = requestFn();
    pendingRequests.current.set(key, request);

    // The .catch() swallows the rejection of the cleanup chain only; the
    // caller still receives the original rejection from `request`.
    request.finally(() => {
      pendingRequests.current.delete(key);
    }).catch(() => {});

    return request;
  }, []);

  // Actions
  const actions = {
    // Fetch projects - always fetches fresh data
    // Skip refetch if triggered by tab visibility change
    fetchProjects: useCallback(async (skipVisibilityCheck = false) => {
      // Skip if triggered by tab becoming visible (after initial mount)
      if (!skipVisibilityCheck && mountedRef.current && checkWasRecentlyHidden()) {
        return state.projects;
      }

      mountedRef.current = true;

      const cacheKey = 'projects';

      return makeRequest(cacheKey, async () => {
        dispatch({ type: ACTIONS.SET_PROJECTS_LOADING, loading: true });

        try {
          const projects = await CoreService.fetchUserProjects();
          dispatch({ type: ACTIONS.SET_PROJECTS_SUCCESS, projects });
          return projects;
        } catch (error) {
          dispatch({ type: ACTIONS.SET_PROJECTS_ERROR, error: error.message });
          throw error;
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [makeRequest]),

    // Fetch targets for a specific project
    fetchTargets: useCallback(async (projectId) => {
      const cacheKey = `targets-${projectId}`;

      return makeRequest(cacheKey, async () => {
        dispatch({ type: ACTIONS.SET_TARGETS_LOADING, projectId, loading: true });

        try {
          const targets = await CoreService.fetchProjectTargets(projectId);
          dispatch({ type: ACTIONS.SET_TARGETS_SUCCESS, projectId, targets });
          return targets;
        } catch (error) {
          dispatch({ type: ACTIONS.SET_TARGETS_ERROR, projectId, error: error.message });
          throw error;
        }
      });
    }, [makeRequest]),

    // Fetch commits for a specific project/target (no caching - always fetch fresh)
    fetchCommits: useCallback(async (projectId, targetName, limit = 50, offset = 0) => {
      const dedupeKey = `commits-${projectId}-${targetName}-${limit}-${offset}`;

      return makeRequest(dedupeKey, async () => {
        try {
          const result = await CoreService.fetchProjectCommits(projectId, targetName, limit, offset);
          return result;
        } catch (error) {
          throw error;
        }
      });
    }, [makeRequest]),

    // Fetch commits by branch commit SHA (no caching - always fetch fresh)
    fetchCommitsByBranch: useCallback(async (projectId, targetName, commitSha, limit = 50, offset = 0, onlyChanges = false) => {
      const dedupeKey = `commits-${projectId}-${targetName}-${commitSha}-${limit}-${offset}-${onlyChanges ? 'changes' : 'all'}`;

      return makeRequest(dedupeKey, async () => {
        try {
          const result = await CoreService.fetchCommitsByBranch(projectId, targetName, commitSha, limit, offset, onlyChanges);
          return result;
        } catch (error) {
          throw error;
        }
      });
    }, [makeRequest]),

    // Create project and optimistically update state
    createProject: useCallback(async (name, description, repoUrl) => {
      try {
        const project = await CoreService.createProject(name, description, repoUrl);
        // Set created project state to show API key
        if (project && project.api_key) {
          dispatch({ type: ACTIONS.SET_CREATED_PROJECT, project });
        }
        // Add the new project to the existing list
        dispatch({ type: ACTIONS.ADD_PROJECT, project });
        return project;
      } catch (error) {
        throw error;
      }
    }, []),

    // Delete project and optimistically update state
    deleteProject: useCallback(async (projectId) => {
      try {
        await CoreService.deleteProject(projectId);
        // Remove the project from the current list
        dispatch({ type: ACTIONS.REMOVE_PROJECT, projectId });
        return true;
      } catch (error) {
        throw error;
      }
    }, []),

    // Fetch heads for a specific project and target
    fetchHeads: useCallback(async (projectId, targetName) => {
      const cacheKey = `${projectId}-${targetName}`;

      return makeRequest(`heads-${cacheKey}`, async () => {
        dispatch({ type: ACTIONS.SET_HEADS_LOADING, key: cacheKey, loading: true });

        try {
          const heads = await CoreService.fetchTargetHeads(projectId, targetName);
          dispatch({ type: ACTIONS.SET_HEADS_SUCCESS, key: cacheKey, heads });
          return heads;
        } catch (error) {
          dispatch({ type: ACTIONS.SET_HEADS_ERROR, key: cacheKey, error: error.message });
          throw error;
        }
      });
    }, [makeRequest]),

    // Fetch memory report for a specific commit
    fetchMemoryReport: useCallback(async (projectId, commitSha, targetName = null) => {
      const cacheKey = commitSha;

      return makeRequest(`memory-report-${cacheKey}`, async () => {
        dispatch({ type: ACTIONS.SET_MEMORY_REPORT_LOADING, commitSha: cacheKey, loading: true });

        try {
          // Get target name if not provided
          let finalTargetName = targetName;
          if (!finalTargetName) {
            const targets = await CoreService.fetchProjectTargets(projectId);
            if (!targets || targets.length === 0) {
              throw new Error('No targets found for this project');
            }
            finalTargetName = targets[0].name;
          }

          const report = await CoreService.fetchMemoryReportByCommit(projectId, finalTargetName, commitSha);
          dispatch({ type: ACTIONS.SET_MEMORY_REPORT_SUCCESS, commitSha: cacheKey, report });

          // Auto-cache the commit message from the memory report to avoid duplicate API calls
          if (report?.commit) {
            dispatch({ type: ACTIONS.SET_COMMIT_MESSAGE_SUCCESS, commitSha: cacheKey, message: report.commit });
          }

          return report;
        } catch (error) {
          dispatch({ type: ACTIONS.SET_MEMORY_REPORT_ERROR, commitSha: cacheKey, error: error.message });
          throw error;
        }
      });
    }, [makeRequest]),

    // Fetch commit message for a specific commit
    fetchCommitMessage: useCallback(async (projectId, commitSha) => {
      const cacheKey = commitSha;

      // Check if we already have the commit message from a memory report
      if (state.commitMessages[cacheKey]) {
        return state.commitMessages[cacheKey];
      }

      // Check if we have the full memory report - extract commit message from it
      if (state.memoryReports[cacheKey]?.commit) {
        const message = state.memoryReports[cacheKey].commit;
        dispatch({ type: ACTIONS.SET_COMMIT_MESSAGE_SUCCESS, commitSha: cacheKey, message });
        return message;
      }

      return makeRequest(`commit-message-${cacheKey}`, async () => {
        dispatch({ type: ACTIONS.SET_COMMIT_MESSAGE_LOADING, commitSha: cacheKey, loading: true });

        try {
          // Get target name for API call
          const targets = await CoreService.fetchProjectTargets(projectId);
          if (!targets || targets.length === 0) {
            throw new Error('No targets found for this project');
          }
          const targetName = targets[0].name;

          const report = await CoreService.fetchMemoryReportByCommit(projectId, targetName, commitSha);
          const message = report?.commit || null;
          dispatch({ type: ACTIONS.SET_COMMIT_MESSAGE_SUCCESS, commitSha: cacheKey, message });
          return message;
        } catch (error) {
          dispatch({ type: ACTIONS.SET_COMMIT_MESSAGE_ERROR, commitSha: cacheKey, error: error.message });
          throw error;
        }
      });
    }, [state.commitMessages, state.memoryReports, makeRequest]),

    // Fetch memory comparison between two commits
    fetchMemoryComparison: useCallback(async (projectId, targetName, baseCommitSha, headCommitSha) => {
      const cacheKey = `${baseCommitSha}-${headCommitSha}`;

      return makeRequest(`memory-comparison-${cacheKey}`, async () => {
        dispatch({ type: ACTIONS.SET_MEMORY_COMPARISON_LOADING, comparisonKey: cacheKey, loading: true });

        try {
          const comparisonData = await CoreService.fetchMemoryDiffByCommits(
            projectId,
            targetName,
            baseCommitSha,
            headCommitSha,
            true  // allSymbols = true for symbol analysis
          );

          // CoreService catches API errors and returns { success: false, error: message }
          // instead of throwing - detect this and treat as an error
          if (comparisonData?.success === false) {
            throw new Error(comparisonData.error || 'Comparison failed');
          }

          dispatch({
            type: ACTIONS.SET_MEMORY_COMPARISON_SUCCESS,
            comparisonKey: cacheKey,
            data: comparisonData
          });

          return comparisonData;
        } catch (error) {
          dispatch({
            type: ACTIONS.SET_MEMORY_COMPARISON_ERROR,
            comparisonKey: cacheKey,
            error: error.message
          });
          // Don't re-throw - error is stored in context state and read by hooks
        }
      });
    }, [makeRequest]),

    // Fetch budgets for a target with calculated usage
    fetchBudgets: useCallback(async (projectId, targetName, commitSha) => {
      const cacheKey = `${projectId}-${targetName}-${commitSha}`;

      return makeRequest(`budgets-${cacheKey}`, async () => {
        dispatch({ type: ACTIONS.SET_BUDGETS_LOADING, key: cacheKey, loading: true });

        try {
          const budgets = await CoreService.fetchBudgets(projectId, targetName, commitSha);
          dispatch({ type: ACTIONS.SET_BUDGETS_SUCCESS, key: cacheKey, budgets });
          return budgets;
        } catch (error) {
          dispatch({ type: ACTIONS.SET_BUDGETS_ERROR, key: cacheKey, error: error.message });
          throw error;
        }
      });
    }, [makeRequest]),

    // Created project actions
    clearCreatedProject: useCallback(() => {
      dispatch({ type: ACTIONS.CLEAR_CREATED_PROJECT });
    }, []),

    // Clear all cached data (useful when navigating away from pages)
    clearAllData: useCallback(() => {
      dispatch({ type: ACTIONS.CLEAR_ALL_DATA });
    }, []),

    // Invalidate all pending requests and cached data to force fresh fetches
    invalidateCache: useCallback(() => {
      // Clear all pending request deduplication in ProjectContext
      pendingRequests.current.clear();
      // Clear all pending requests in apiClient
      clearPendingRequests();
      // Clear all cached data
      dispatch({ type: ACTIONS.CLEAR_ALL_DATA });
    }, [])
  };

  const value = {
    // State
    projects: state.projects,
    targets: state.targets,
    heads: state.heads,
    memoryReports: state.memoryReports,
    commitMessages: state.commitMessages,
    memoryComparisons: state.memoryComparisons,
    budgets: state.budgets,
    loading: state.loading,
    errors: state.errors,
    // Created project state
    createdProject: state.createdProject,
    // Actions
    ...actions
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

// Custom hook
export function useProjects() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
}

export default ProjectContext;
